import type { CSSProperties } from "react";

import { getOffsetOriginConfig } from "@skilitics/drag-drop";

import type { LayoutSettings, Orientation } from "../types.ts";
import { stripDefaultValues } from "../utils.ts";

// trying to reduce size of configs by removing properties with default values

export const unsetPropertiesWithDefaultValues = (values: CSSProperties, defaultValues: NonNullable<CSSProperties>) => {
  const newValues = Object.fromEntries(
    Object.entries(values ?? {}).filter(([name, value]) => value !== defaultValues[name as keyof typeof defaultValues]),
  );
  if (Object.keys(newValues).length === 0) return undefined;
  return newValues as CSSProperties;
};

export const formatOverridePropertiesForConfig = (
  newCssProperties: CSSProperties,
  configCssProperties: CSSProperties,
) => {
  // Strip css properties that are the same as the defaults.
  const omittedValues = stripDefaultValues(newCssProperties, configCssProperties);

  // iterate properties in ommittedValues and change value to "unset" if it is undefined.
  // Don't need to "unset" if the value is undefined in default config.
  return Object.fromEntries(
    Object.entries(omittedValues)
      .filter(([key, value]) => !!value || (!value && key in configCssProperties))
      .map(([key, value]) => [key, value ?? "unset"]),
  );
};

export const combineCssWithOverrides = (
  configCssProperties: CSSProperties,
  overrideCssProperties?: CSSProperties,
): Partial<CSSProperties> => {
  if (overrideCssProperties) {
    // flatten the element state properties
    const flattenedCssProperties = {
      ...configCssProperties,
      ...overrideCssProperties,
    };
    // iterate properties and change "unset" values to undefined.
    return Object.fromEntries(
      Object.entries(flattenedCssProperties).map(([key, value]) => [key, value === "unset" ? undefined : value]),
    );
  }
  return configCssProperties;
};

export const transformLayoutSettingsToCss = (
  configCssProperties: CSSProperties,
  layoutSettings: LayoutSettings,
  parentOrientation: Orientation,
  enableFitContent: boolean,
): CSSProperties => {
  const cssProperties: CSSProperties = { ...configCssProperties };

  // Flex properties
  if (parentOrientation === "horizontal") {
    if (layoutSettings.horizontalBehavior === "fixed") {
      cssProperties.flexGrow = 0;
      cssProperties.flexShrink = 0;
      cssProperties.flexBasis = cssProperties.width ?? "auto";
    } else if (layoutSettings.horizontalBehavior === "fit") {
      cssProperties.flexGrow = 0;
      cssProperties.flexShrink = 0;
      cssProperties.flexBasis = "auto";
    } else if (layoutSettings.horizontalBehavior === "fill") {
      cssProperties.flexGrow = 1;
      cssProperties.flexShrink = 1;
      cssProperties.flexBasis = "100%";
    }
  }

  if (parentOrientation === "vertical") {
    if (layoutSettings.verticalBehavior === "fixed") {
      cssProperties.flexGrow = 0;
      cssProperties.flexShrink = 0;
      cssProperties.flexBasis = cssProperties.height ?? "auto";
    } else if (layoutSettings.verticalBehavior === "fit") {
      cssProperties.flexGrow = 0;
      cssProperties.flexShrink = 0;
      cssProperties.flexBasis = "auto";
    } else if (layoutSettings.verticalBehavior === "fill") {
      cssProperties.flexGrow = 1;
      cssProperties.flexShrink = 1;
      cssProperties.flexBasis = "100%";
    }
  }

  // Width / Height based on flex behaviour.
  if (layoutSettings.horizontalBehavior === "fit") {
    cssProperties.width = enableFitContent ? "auto" : cssProperties.width;
  } else if (layoutSettings.horizontalBehavior === "fill") {
    cssProperties.width = "100%";
  }

  if (layoutSettings.verticalBehavior === "fit") {
    cssProperties.height = enableFitContent ? "auto" : cssProperties.height;
  } else if (layoutSettings.verticalBehavior === "fill") {
    cssProperties.height = "100%";
  }

  // Offset origin centering for absolutely positioned elements.
  // Sets inset: 0 + margin: auto on centered axes.
  if (layoutSettings.offsetOrigin) {
    const originConfig = getOffsetOriginConfig(layoutSettings.offsetOrigin);

    if (originConfig.horizontalCenter) {
      cssProperties.left = cssProperties.left ?? "0";
      cssProperties.right = cssProperties.right ?? "0";
      cssProperties.marginLeft = "auto";
      cssProperties.marginRight = "auto";
    }

    if (originConfig.verticalCenter) {
      cssProperties.top = cssProperties.top ?? "0";
      cssProperties.bottom = cssProperties.bottom ?? "0";
      cssProperties.marginTop = "auto";
      cssProperties.marginBottom = "auto";
    }
  }

  return cssProperties;
};

interface AlignmentConfig {
  justify: "start" | "center" | "end";
  align: "start" | "center" | "end";
}

const getAlignmentConfig = (alignment: LayoutSettings["alignment"]): AlignmentConfig => {
  const alignmentMap: Record<NonNullable<LayoutSettings["alignment"]>, AlignmentConfig> = {
    alignBottomRight: { justify: "end", align: "end" },
    alignBottomCenter: { justify: "center", align: "end" },
    alignBottomLeft: { justify: "start", align: "end" },
    alignRight: { justify: "end", align: "center" },
    alignCenter: { justify: "center", align: "center" },
    alignLeft: { justify: "start", align: "center" },
    alignTopRight: { justify: "end", align: "start" },
    alignTopCenter: { justify: "center", align: "start" },
    alignTopLeft: { justify: "start", align: "start" },
  };

  return alignment ? alignmentMap[alignment] : { justify: "start", align: "start" };
};

const mapToFlexValue = (value: "start" | "center" | "end"): string => {
  const flexMap = { start: "flex-start", center: "center", end: "flex-end" };
  return flexMap[value];
};

const reverseAlignment = (align: "start" | "center" | "end"): "start" | "center" | "end" => {
  const reverseMap: Record<"start" | "center" | "end", "start" | "center" | "end"> = {
    start: "end",
    center: "center",
    end: "start",
  };
  return reverseMap[align];
};

const transformFlexAlignment = (
  cssProperties: CSSProperties,
  config: AlignmentConfig,
  flowDirection: "horizontal" | "vertical",
  flowReverse: boolean,
): CSSProperties => {
  if (flowDirection === "horizontal") {
    const mainAxis = flowReverse ? reverseAlignment(config.justify) : config.justify;
    const crossAxis = config.align;
    return {
      ...cssProperties,
      justifyContent: mapToFlexValue(mainAxis),
      alignItems: mapToFlexValue(crossAxis),
      alignContent: mapToFlexValue(crossAxis),
    };
  }

  const mainAxis = flowReverse ? reverseAlignment(config.align) : config.align;
  const crossAxis = config.justify;
  return {
    ...cssProperties,
    justifyContent: mapToFlexValue(mainAxis),
    alignItems: mapToFlexValue(crossAxis),
    alignContent: mapToFlexValue(crossAxis),
  };
};

const transformGridAlignment = (cssProperties: CSSProperties, config: AlignmentConfig): CSSProperties => ({
  ...cssProperties,
  justifyItems: config.justify,
  alignItems: config.align,
  justifyContent: config.justify,
  alignContent: config.align,
  gridTemplate: "1fr / 1fr",
});

export const transformAlignmentToCss = (
  configCssProperties: CSSProperties,
  layoutSettings: LayoutSettings,
): CSSProperties => {
  const cssProperties: CSSProperties = { ...configCssProperties };

  const alignmentConfig = getAlignmentConfig(layoutSettings.alignment);

  if (cssProperties.display === "grid") {
    return transformGridAlignment(cssProperties, alignmentConfig);
  }

  const flowDirection = cssProperties.flexDirection?.includes("column") ? "vertical" : "horizontal";
  const flowReverse = cssProperties.flexDirection === "row-reverse" || cssProperties.flexDirection === "column-reverse";

  return transformFlexAlignment(cssProperties, alignmentConfig, flowDirection, flowReverse);
};
